// src/tools/basemap/basemapTool.js
import maplibregl from 'maplibre-gl';
import { center as turfCenter, distance as turfDistance, bearing as turfBearing, destination as turfDestination } from '@turf/turf';

const LOCAL_STORAGE_KEY = 'basemap_overlay_data';

export const basemapTool = {
    _map: null,
    _onOverlayUpdate: null,
    _fileInput: null,
    _overlayData: null, // { image_url, coordinates, opacity }
    _markers: [],
    _centerMarker: null,
    _isDragging: false,
    _draggedMarker: null,
    _dragStartCoords: null,
    _dragStartMouse: null,
    _controlPanel: null,
    _originalCoordinates: null,

    initialize(map, options) {
        this._map = map;
        this._onOverlayUpdate = options.onOverlayUpdate;

        // Привязываем обработчики событий к текущему экземпляру один раз
        this.onCornerMouseMove = this.onCornerMouseMove.bind(this);
        this.onCenterMouseMove = this.onCenterMouseMove.bind(this);
        this.onMouseUp = this.onMouseUp.bind(this);

        // Загружаем сохраненные данные при инициализации
        const savedData = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (savedData) {
            try {
                this._overlayData = JSON.parse(savedData);
                this._onOverlayUpdate(this._overlayData);
            } catch (e) {
                console.error('Failed to parse saved overlay data:', e);
            }
        }

        // Создаем скрытый инпут для файлов
        this._fileInput = document.createElement('input');
        this._fileInput.type = 'file';
        this._fileInput.accept = 'image/*';
        this._fileInput.style.display = 'none';
        this._fileInput.onchange = this.handleFileChange.bind(this);
        document.body.appendChild(this._fileInput);
    },

    activate() {
        console.log('Basemap tool activated');
        this._originalCoordinates = this._overlayData ? JSON.parse(JSON.stringify(this._overlayData.coordinates)) : null;
        if (this._overlayData) {
            this.showControls();
        } else {
            this._fileInput.click();
        }
    },

    deactivate() {
        console.log('Basemap tool deactivated');
        if (this._map.dragPan.isEnabled() === false) {
            this._map.dragPan.enable();
        }
        this.cleanup();
    },

    cleanup() {
        this._markers.forEach(m => m.remove());
        this._markers = [];
        if (this._centerMarker) {
            this._centerMarker.remove();
            this._centerMarker = null;
        }
        if (this._controlPanel) {
            this._controlPanel.remove();
            this._controlPanel = null;
        }
    },

    handleFileChange(e) {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
                const imageUrl = event.target.result;
                const bounds = this._map.getBounds();
                const west = bounds.getWest();
                const east = bounds.getEast();
                const north = bounds.getNorth();
                const south = bounds.getSouth();
                const center = this._map.getCenter();

                // Начальные координаты - 50% от видимой области
                const width = (east - west) * 0.5;
                const height = (north - south) * 0.5;

                this._overlayData = {
                    image_url: imageUrl,
                    coordinates: [
                        [center.lng - width / 2, center.lat + height / 2], // top-left
                        [center.lng + width / 2, center.lat + height / 2], // top-right
                        [center.lng + width / 2, center.lat - height / 2], // bottom-right
                        [center.lng - width / 2, center.lat - height / 2]  // bottom-left
                    ],
                    opacity: 0.75
                };
                this._originalCoordinates = JSON.parse(JSON.stringify(this._overlayData.coordinates));
                console.log('[basemapTool] Overlay data created, calling onOverlayUpdate');
                if (this._onOverlayUpdate) {
                    this._onOverlayUpdate(this._overlayData);
                }
                this.showControls();
            };
            reader.readAsDataURL(file);
        }
        this._fileInput.value = '';
    },

    showControls() {
        console.log('[basemapTool] showControls called');
        this.cleanup();
        if (!this._overlayData) {
            console.warn('[basemapTool] No overlay data, cannot show controls');
            return;
        }

        // Устанавливаем начальную прозрачность, если ее нет
        if (this._overlayData.opacity === undefined) {
            this._overlayData.opacity = 0.75;
        }

        console.log('[basemapTool] Creating markers for corners:', this._overlayData.coordinates);

        // Создаем маркеры для углов
        const cornerStyles = {
            width: '16px',
            height: '16px',
            backgroundColor: 'rgba(100, 100, 255, 0.9)',
            border: '2px solid white',
            borderRadius: '50%',
            cursor: 'nwse-resize',
            boxShadow: '0 0 8px rgba(0,0,0,0.6)'
        };

        this._overlayData.coordinates.forEach((coord, index) => {
            const el = document.createElement('div');
            Object.assign(el.style, cornerStyles);
            el.title = `Угол ${index + 1}`;

            const marker = new maplibregl.Marker({ element: el, draggable: false })
                .setLngLat(coord)
                .addTo(this._map);

            el.addEventListener('mousedown', (e) => this.onCornerMouseDown(e, marker, index));
            this._markers.push(marker);
        });

        console.log('[basemapTool] Corner markers created:', this._markers.length);

        // Маркер в центре для перемещения всей подложки
        const center = turfCenter({ type: 'Polygon', coordinates: [this._overlayData.coordinates] }).geometry.coordinates;
        const centerEl = document.createElement('div');
        Object.assign(centerEl.style, {
            ...cornerStyles,
            backgroundColor: 'rgba(255, 100, 100, 0.9)',
            cursor: 'move'
        });
        centerEl.title = 'Переместить подложку';

        this._centerMarker = new maplibregl.Marker({ element: centerEl, draggable: false })
            .setLngLat(center)
            .addTo(this._map);
        centerEl.addEventListener('mousedown', (e) => this.onCenterMouseDown(e));

        console.log('[basemapTool] Center marker created at:', center);

        // Панель управления
        this._controlPanel = document.createElement('div');
        this._controlPanel.style.cssText = `
            position: absolute;
            top: 10px;
            left: 10px;
            background: rgba(255, 255, 255, 0.95);
            padding: 10px 15px;
            border-radius: 8px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.2);
            z-index: 1000;
            display: flex;
            gap: 15px;
            align-items: center;
            font-family: sans-serif;
            font-size: 12px;
        `;

        this._controlPanel.innerHTML = `
            <div style="display: flex; align-items: center; gap: 8px;">
                <label for="opacity-slider">Прозрачность:</label>
                <input type="range" id="opacity-slider" min="0" max="1" step="0.05" value="${this._overlayData.opacity}" style="width: 100px;">
                <span id="opacity-value">${Math.round(this._overlayData.opacity * 100)}%</span>
            </div>
            <div style="display: flex; gap: 5px;">
                <button id="btn-zoom-in" title="Увеличить">+</button>
                <button id="btn-zoom-out" title="Уменьшить">-</button>
            </div>
            <div style="display: flex; gap: 5px;">
                <button id="btn-save" title="Сохранить">Сохранить</button>
                <button id="btn-cancel" title="Отменить">Отмена</button>
                <button id="btn-remove" title="Удалить">Удалить</button>
            </div>
        `;

        const container = this._map.getContainer();
        if (container) {
            container.appendChild(this._controlPanel);
            console.log('[basemapTool] Control panel added to map container');
        } else {
            console.error('[basemapTool] Map container not found!');
        }

        // Обработчики панели
        const slider = this._controlPanel.querySelector('#opacity-slider');
        const opacityValue = this._controlPanel.querySelector('#opacity-value');
        if (slider && opacityValue) {
            slider.oninput = (e) => {
                const value = parseFloat(e.target.value);
                console.log('[basemapTool] Opacity changed to:', value);
                this._overlayData.opacity = value;
                opacityValue.textContent = Math.round(value * 100) + '%';
                if (this._onOverlayUpdate) {
                    this._onOverlayUpdate(this._overlayData);
                }
            };
        } else {
            console.error('[basemapTool] Slider or opacity value element not found!');
        }

        this._controlPanel.querySelector('#btn-zoom-in').onclick = () => this.scaleOverlay(1.02);
        this._controlPanel.querySelector('#btn-zoom-out').onclick = () => this.scaleOverlay(0.98);
        this._controlPanel.querySelector('#btn-save').onclick = () => this.save();
        this._controlPanel.querySelector('#btn-cancel').onclick = () => this.cancel();
        this._controlPanel.querySelector('#btn-remove').onclick = () => this.remove();
    },

    onCornerMouseDown(e, marker, index) {
        e.preventDefault();
        e.stopPropagation();
        this._isDragging = true;
        this._draggedMarker = marker;
        this._dragStartCoords = {
            cornerIndex: index,
            coordinates: JSON.parse(JSON.stringify(this._overlayData.coordinates))
        };
        
        // Конвертируем пиксельные координаты в географические
        const rect = this._map.getCanvas().getBoundingClientRect();
        const point = [e.clientX - rect.left, e.clientY - rect.top];
        this._dragStartMouse = this._map.unproject(point);
        
        // The event listeners are already bound in initialize, so no need to re-bind.
        // Also, the mousemove event should use the map's event object directly for lngLat.
        this._map.on('mousemove', this.onCornerMouseMove);
        this._map.on('mouseup', this.onMouseUp);
    },

    onCenterMouseDown(e) {
        // This is for the center marker, so it should use e.lngLat directly.
        e.preventDefault();
        e.stopPropagation();
        this._isDragging = true;
        this._draggedMarker = 'center';
        this._dragStartCoords = {
            coordinates: JSON.parse(JSON.stringify(this._overlayData.coordinates))
        };
        
        this._dragStartMouse = this._map.unproject([e.clientX, e.clientY]); // Get initial mouse position in LngLat
        
        this._map.getCanvas().style.cursor = 'grabbing';
        this._map.dragPan.disable();

        this._map.on('mousemove', this.onCenterMouseMove);
        this._map.on('mouseup', this.onMouseUp);
    },

    onCornerMouseMove(e) {
        if (!this._isDragging || this._draggedMarker === 'center') return;

        const { cornerIndex, coordinates } = this._dragStartCoords;

        // Use e.lngLat directly from the MapMouseEvent
        const currentLngLat = e.lngLat;
        
        // Use turfCenter for consistency
        const center = turfCenter({ type: 'Polygon', coordinates: [coordinates] }).geometry.coordinates;
        const initialMarkerCoord = coordinates[cornerIndex];

        const isRotating = e.originalEvent.shiftKey;
        const isScaling = e.originalEvent.ctrlKey;

        let updatedCoordinates = JSON.parse(JSON.stringify(coordinates)); // Копируем начальные координаты

        if (isRotating || isScaling) {
            // Вычисляем начальный азимут и расстояние от центра до перетаскиваемого угла
            const initialBearing = turfBearing(center, initialMarkerCoord);
            const initialDistance = turfDistance(center, initialMarkerCoord);

            // Вычисляем текущий азимут и расстояние от центра до текущей позиции мыши
            const currentBearing = turfBearing(center, [currentLngLat.lng, currentLngLat.lat]);
            const currentDistance = turfDistance(center, [currentLngLat.lng, currentLngLat.lat]);

            if (isRotating) {
                const rotateAngle = currentBearing - initialBearing; // Разница в угле
                updatedCoordinates = coordinates.map(coord => {
                    const b = turfBearing(center, coord);
                    const d = turfDistance(center, coord);
                    const dest = turfDestination(center, d, b + rotateAngle);
                    return dest.geometry.coordinates;
                });
            } else if (isScaling) {
                const scaleFactor = currentDistance / initialDistance;
                updatedCoordinates = coordinates.map(coord => {
                    const b = turfBearing(center, coord);
                    const d = turfDistance(center, coord);
                    const dest = turfDestination(center, d * scaleFactor, b);
                    return dest.geometry.coordinates;
                });
            }
        } else {
            // Простое перемещение угла (изменение геометрии)
            updatedCoordinates[cornerIndex] = [currentLngLat.lng, currentLngLat.lat];
        }
        this._overlayData.coordinates = updatedCoordinates;
        this.updateOverlayAndMarkers();
    },

    onCenterMouseMove(e) {
        if (!this._isDragging || this._draggedMarker !== 'center') return;

        const { coordinates } = this._dragStartCoords;

        // Use e.lngLat directly from the MapMouseEvent
        const currentLngLat = e.lngLat;
        
        const deltaLng = currentLngLat.lng - this._dragStartMouse.lng;
        const deltaLat = currentLngLat.lat - this._dragStartMouse.lat;

        // Перемещаем все углы на одинаковый вектор
        this._overlayData.coordinates = coordinates.map(coord => [
            coord[0] + deltaLng,
            coord[1] + deltaLat
        ]);

        this.updateOverlayAndMarkers();
    },

    onMouseUp() {
        if (!this._isDragging) return;

        this._isDragging = false;
        this._draggedMarker = null;
        this._dragStartCoords = null;
        this._dragStartMouse = null;
        this._map.getCanvas().style.cursor = ''; // Reset cursor
        this._map.off('mousemove', this.onCornerMouseMove);
        this._map.off('mousemove', this.onCenterMouseMove);
        this._map.off('mouseup', this.onMouseUp);
        this._map.dragPan.enable();
    },

    scaleOverlay(factor) {
        if (!this._overlayData) {
            console.warn('[basemapTool] scaleOverlay called but no overlay data');
            return;
        }

        console.log('[basemapTool] scaleOverlay with factor:', factor);
        const center = turfCenter({ type: 'Polygon', coordinates: [this._overlayData.coordinates] }).geometry.coordinates;
        const newCoordinates = this._overlayData.coordinates.map(coord => {
            const dx = coord[0] - center[0];
            const dy = coord[1] - center[1];
            return [center[0] + dx * factor, center[1] + dy * factor];
        });

        this._overlayData.coordinates = newCoordinates;
        if (this._onOverlayUpdate) {
            this._onOverlayUpdate(this._overlayData);
        }
        this.updateOverlayAndMarkers();
    },

    updateOverlayAndMarkers() {
        console.log('[basemapTool] updateOverlayAndMarkers called');
        // Обновляем подложку
        if (this._onOverlayUpdate && this._overlayData) {
            this._onOverlayUpdate(this._overlayData);
        }

        // Обновляем позиции маркеров
        this._markers.forEach((marker, index) => {
            if (this._overlayData && this._overlayData.coordinates[index]) {
                marker.setLngLat(this._overlayData.coordinates[index]);
            }
        });

        // Обновляем центральный маркер
        if (this._centerMarker && this._overlayData) {
            const center = turfCenter({ type: 'Polygon', coordinates: [this._overlayData.coordinates] }).geometry.coordinates;
            this._centerMarker.setLngLat(center);
        }
    },

    save() {
        if (this._overlayData) {
            localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(this._overlayData));
            this._onOverlayUpdate(this._overlayData);
            this.deactivate();
        }
    },

    cancel() {
        if (this._originalCoordinates) {
            this._overlayData.coordinates = this._originalCoordinates;
            this._onOverlayUpdate(this._overlayData);
            this.updateOverlayAndMarkers();
        } else {
            this._onOverlayUpdate(null);
            this._overlayData = null;
        }
        this.deactivate();
    },

    remove() {
        localStorage.removeItem(LOCAL_STORAGE_KEY);
        this._onOverlayUpdate(null);
        this._overlayData = null;
        this._originalCoordinates = null;
        this.deactivate();
    }
};